import { combineEpics, Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { resetGridData } from '../../actions/grid-screen';
import {
    ACCEPT_SUGGESTED_ORGANIZATION_IN_EDITOR,
    AcceptSuggestedOrganizationInEditor,
    BULK_UPDATE_ORGANIZATIONS,
    BulkUpdateOrganizationsAction,
    closeOrganizationsBulkEditor,
    INIT_ORGANIZATIONS_EDITORS,
    InitOrganizationsEditors,
    LOAD_ORGANIZATION_EDITOR_DATA,
    loadOrganizationEditorData,
    LoadOrganizationEditorData,
    RESET_ORGANIZATIONS_EDITOR,
    ResetOrganizationsEditor,
    saveOrganizationToEditor,
    UPDATE_ORGANIZATION_EDITOR_ERRORS,
    UpdateOrganizationEditorErrors,
    updateOrganizationsEditorsData,
    updateOrganizationsEditorsDataStatus
} from '../../actions/organizationsEditor';
import { OrganizationErrorCodes } from '../../declarations/organizations/organizations';
import {
    extractUniqOrganizationIds,
    mapEditorKeysToOrganisations,
    mapOrganizationDataFromApiFormat
} from '../../mappers/organization/organization';
import { selectOrganizationEditableIds } from '../../selectors/bannerEdit/massActions/massActions';
import {
    getBannersOrganizationSavedResultMessage,
    showBannersPartialUpdateMessage
} from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { BannerId } from '../../typings/banners';
import { MassActionId } from '../../typings/bannersEditState';
import { DnaEpic } from '../../typings/epic';
import { GdUpdateOrganizationInput, OrganizationsQuery } from '../../typings/graphql-grid';
import { OrganizationData, OrganizationEditorParams, OrganizationsEditorDataStatus } from '../../typings/organizations';
import { State } from '../../typings/state';

const loadOrganizationsForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(INIT_ORGANIZATIONS_EDITORS),
        mergeMap(
            (action: InitOrganizationsEditors): Observable<Action> => {
                const { login } = store$.value.commonData;
                const { params } = action;
                const orgIds: string[] = extractUniqOrganizationIds(params);

                const editorsWithOrganization: OrganizationEditorParams[] = params.filter(
                    p => p.organizationId || p.suggestedOrganizationId
                );

                if (!orgIds.length) {
                    return Observable.empty();
                }

                return from(deps.organizationDataProvider.getOrganizations(login, orgIds)).pipe(
                    mergeMap(
                        (data: OrganizationsQuery): Observable<Action> => {
                            const organizations: Record<string, OrganizationData> | undefined =
                                data.organizations && mapOrganizationDataFromApiFormat(data);

                            const editorsOrganizations: Record<string, OrganizationData> = mapEditorKeysToOrganisations(
                                editorsWithOrganization,
                                organizations
                            );

                            return of(updateOrganizationsEditorsData(editorsOrganizations));
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            const editorsStates = editorsWithOrganization.reduce((acc, editorParams) => {
                                const { editorKey } = editorParams;
                                return {
                                    ...acc,
                                    [editorKey]: OrganizationsEditorDataStatus.ERROR
                                };
                            }, {});

                            return of(updateOrganizationsEditorsDataStatus(editorsStates));
                        }
                    )
                );
            }
        )
    );
};

const loadOrganizationForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(LOAD_ORGANIZATION_EDITOR_DATA),
        mergeMap(
            (action: LoadOrganizationEditorData): Observable<Action> => {
                const { login } = store$.value.commonData;
                const { editorParams } = action;
                const { editorKey, organizationId } = editorParams;

                if (!organizationId) {
                    return Observable.empty();
                }

                return from(deps.organizationDataProvider.getOrganizations(login, [organizationId])).pipe(
                    mergeMap(
                        (data: OrganizationsQuery): Observable<Action> => {
                            const organizations: Record<string, OrganizationData> | undefined =
                                data.organizations && mapOrganizationDataFromApiFormat(data);

                            const editorsOrganizations: Record<string, OrganizationData> = mapEditorKeysToOrganisations(
                                [editorParams],
                                organizations
                            );

                            return of(updateOrganizationsEditorsData(editorsOrganizations));
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            return of(
                                updateOrganizationsEditorsDataStatus({
                                    [editorKey]: OrganizationsEditorDataStatus.ERROR
                                })
                            );
                        }
                    )
                );
            }
        )
    );
};

const resetOrganizationsEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(RESET_ORGANIZATIONS_EDITOR),
        map((action: ResetOrganizationsEditor) => {
            const { editorKey, organizationId } = action.editorParams;
            const organization: OrganizationData | undefined = organizationId
                ? store$.value.organizationsData && store$.value.organizationsData[organizationId]
                : undefined;

            // случай, когда организация была не выбрана
            if (!organizationId) {
                return updateOrganizationsEditorsDataStatus({
                    [editorKey]: OrganizationsEditorDataStatus.EMPTY
                });
            }

            // случай, когда организация была выбрана и данные о ней были загружены без ошибок
            if (organization) {
                return updateOrganizationsEditorsDataStatus({
                    [editorKey]: OrganizationsEditorDataStatus.LOADED
                });
            }

            // В случае, когда есть ID, но нет данных
            // (например при смене ненайденной организации на новую)
            // делаем дополнительный запрос, чтобы компонент получил правильный статус данных
            return loadOrganizationEditorData({
                editorKey,
                organizationId
            });
        })
    );
};

const updateOrganizationEditorErrorsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_ORGANIZATION_EDITOR_ERRORS),
        switchMap((action: UpdateOrganizationEditorErrors) => {
            const { editorKey, errors } = action;

            if (errors.length === 1 && errors[0].code === OrganizationErrorCodes.NOT_FOUND) {
                return of(
                    updateOrganizationsEditorsDataStatus({ [editorKey]: OrganizationsEditorDataStatus.NOT_FOUND })
                );
            }

            return of(updateOrganizationsEditorsDataStatus({ [editorKey]: OrganizationsEditorDataStatus.ERROR }));
        })
    );
};

const bulkUpdateOrganizationsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(BULK_UPDATE_ORGANIZATIONS),
        mergeMap(
            (action: BulkUpdateOrganizationsAction): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;
                const selectedBanners = selectOrganizationEditableIds(state);
                const inputData = selectedBanners.map(
                    (id: BannerId): GdUpdateOrganizationInput => ({
                        bannerId: id,
                        permalinkId: action.organizationId || null
                    })
                );

                return from(deps.organizationMutationExecutor.bulkUpdateOrganizations(inputData, login)).pipe(
                    mergeMap(result => {
                        const { updateOrganizations } = result;
                        const { validationResult } = updateOrganizations;

                        const updatedBanners = updateOrganizations.updatedAds;
                        const updatedBannersCount = updatedBanners.length;
                        const selectedBannersCount = selectedBanners.length;
                        const unsavedBannersCount = selectedBannersCount - updatedBannersCount;
                        const validationErrors = validationResult && validationResult.errors;
                        const resultMessage = getBannersOrganizationSavedResultMessage(
                            updatedBannersCount,
                            unsavedBannersCount,
                            validationErrors
                        );
                        const updatedBannerIds = updatedBanners.map(banner => (banner ? banner.id.toString() : ''));

                        showBannersPartialUpdateMessage(
                            resultMessage,
                            selectedBanners,
                            updatedBannerIds,
                            state.commonData.login
                        );

                        return [resetGridData(), closeOrganizationsBulkEditor(MassActionId.bannerGrid)];
                    })
                );
            }
        )
    );
};

const acceptSuggestedOrganizationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ACCEPT_SUGGESTED_ORGANIZATION_IN_EDITOR),
        map((action: AcceptSuggestedOrganizationInEditor) => {
            return saveOrganizationToEditor(action.editorParams);
        })
    );
};

export const organizationsEpics = combineEpics(
    loadOrganizationsForEditEpic,
    loadOrganizationForEditEpic,
    resetOrganizationsEditorEpic,
    bulkUpdateOrganizationsEpic,
    acceptSuggestedOrganizationEpic,
    updateOrganizationEditorErrorsEpic
);
